import AuthBase from "../AuthBase";
import {Grid} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useContext, useRef, useState} from "react";
import Loading from "../../components/Loading";
import DinoDataGrid from "../../components/DataGrid";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import DetailDrawer from "../../components/DetailDrawer";
import ReservationDetail from "../reservation/ReservationDetail";
import DinoDialog from "../../components/Dialog";
import DialogDetail from "../reservation/DialogDetail";
import ReserveBase from "../../components/reserve/ReserveBase";
import SelectOnlyDate from "../../components/reserve/SelectOnlyDate";
import {ReservationProvider} from "../../context/ReservationContext";
import {useSnackbar} from "notistack";
import ReceiptRoundedIcon from "@mui/icons-material/ReceiptRounded";
import InvoiceAdditionalData from "../reservation/components/InvoiceAdditionalData";
import WelcomeCard from "../../components/WelcomeCard";
import useReservationQueries from "../../hooks/queries/useReservationQueries";
import useBusinessQueries from "../../hooks/queries/useBusinessQueries";
import {downloadFileFromResponse} from "../../utils/misc";
import {getColumns} from "../../components/reserve/reservationTable";
import ServiceStats from "../../components/ServiceStats";
import ReservationStats from "../../components/ReservationStats";
import {hasPermission} from "../../utils/permissionUtils";
import {AuthContext} from "../../context/AuthContext";
import NoItems from "../../components/NoItems";

const Dashboard = () => {
    const snackbar = useSnackbar()
    const [newDate, setNewDate] = useState({})
    const [detailView, setDetailView] = useState(false)
    const [reservation, setReservation] = useState({})
    const [billingOpen, setBillingOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [newReservationOpen, setNewReservationOpen] = useState(false)
    const [modifyTimeOpen, setModifyTimeOpen] = useState(false)
    const {getUser} = useContext(AuthContext)
    const user = getUser()

    const {
        useReservationsUserQuery,
        deleteReservationMutation,
        acceptReservationMutation,
        rejectReservationMutation,
        createInvoiceMutation,
        updateReservationMutation,
        sendInvoiceMutation,
        downloadInvoiceMutation,
    } = useReservationQueries();

    const {
        businessGetServiceStatsQuery,
        businessGetReservationStatsQuery,
    } = useBusinessQueries();

    const { data: reservations, isPending: reservationsLoading } = useReservationsUserQuery;
    const { data: reservationStats, isPending: resStatsLoading } = businessGetReservationStatsQuery;
    const { data: serviceStats, isPending: statsLoading } = businessGetServiceStatsQuery;


    const billingRef = useRef(null)

    const findReservation = id => reservations.find(r => r.id === id)

    const deleteReservation = (id) => deleteReservationMutation.mutate(id, {
        onSuccess: response => {
            setDetailView(false)
        }
    })

    const updateDate = (id, date, slot) => updateReservationMutation.mutate({id, date, slot}, {
        onSuccess: response => {
            setDetailView(false)
            setModifyTimeOpen(false)
        }
    })

    const accept = id => acceptReservationMutation.mutate(id, {
        onSuccess: response => {
            setDetailView(false)
        }
    })

    const reject = id => rejectReservationMutation.mutate(id, {
        onSuccess: response => {
            setDetailView(false)
        }
    })

    const createInvoice = (id, data) => createInvoiceMutation.mutate({id, data}, {
        onSuccess: response => {
            setBillingOpen(false)
        }
    })

    const downloadInvoice = (id, doc_id) => downloadInvoiceMutation.mutate({id, doc_id}, {
        onSuccess: response => {
            downloadFileFromResponse(response, 'invoice.pdf');
            setDetailView(false);
        },
    })

    const sendInvoice = (id, doc_id) => sendInvoiceMutation.mutate({id, doc_id})


    const onDeleteClicked = (id) => {
        setReservation(findReservation(id))
        setDeleteOpen(true)
    }

    const onDetailClicked = (id) => {
        setReservation(findReservation(id))
        setDetailView(true)
    }

    const dialogActions = <>
        <Button variant='outlined' style={{padding: 15}} onClick={() => setDeleteOpen(false)}>Mégsem</Button>
        <Button variant='contained' style={{padding: 15}} onClick={() => {
            setDeleteOpen(false)
            deleteReservation(reservation?.id)
        }} endIcon={<DeleteIcon/>}>Foglalás törlése</Button>
    </>

    const modifyActions = <>
        <Button variant='outlined' style={{padding: 15}} onClick={() => setModifyTimeOpen(false)}>Mégsem</Button>
        <Button variant='contained' style={{padding: 15}} onClick={() => {
            const {id, date, slot} = newDate
            updateDate(id, date, slot)

        }} endIcon={<CalendarTodayRoundedIcon/>}>Időpont módosítása</Button>
    </>

    const billingActions = <>
        <Button variant='outlined' style={{padding: 15}} onClick={() => setBillingOpen(false)}>Mégsem</Button>
        <Button variant='contained' style={{padding: 15}} onClick={() => {
            const data = billingRef.current.getData()
            if (data) {
                createInvoice(reservation?.id, data)
                setBillingOpen(false)
            }

        }} endIcon={<ReceiptRoundedIcon/>}>Számla kiállítása</Button>
    </>

    if (reservationsLoading) return <Loading isLoading />

    return <AuthBase label='Vezérlőpult'>
        <div className='main'>
            {hasPermission(user, 'stats', 'view') && <Grid container spacing={2} style={{marginBottom: '20px'}}>
                <Grid item xs={12} md={6}>
                    <div className='dashboard-card'>
                        <Typography variant='headline6'>Szolgáltatások</Typography>
                        <Typography variant='body1' color='#536B74' marginBottom={3}>Az alábbi diagramon látszódank az
                            eddig beérkezett foglalások szolgáltatások szerint.</Typography>
                        <ServiceStats serviceStats={serviceStats}/>
                    </div>
                </Grid>
                <Grid item xs={12} md={6}>
                    <div className='dashboard-card'>
                        <Typography variant='headline6'>Foglalások</Typography>
                        <Typography variant='body1' color='#536B74' marginBottom={3}>Foglalások havi
                            bontásban.</Typography>
                        <ReservationStats reservationStats={reservationStats}/>
                    </div>
                </Grid>
            </Grid>}
            <Typography variant='headline6'>Legfrissebb foglalások</Typography>
            <Typography variant='body1' color='#536B74' gutterBottom>Foglalások időrendben.</Typography>
            {reservations?.length !== 0 ? <DinoDataGrid columns={getColumns(onDeleteClicked, onDetailClicked)} rows={reservations}/> : <NoItems title='Foglalások' description='Jelenleg nincsenek foglalásaid!'/>}
        </div>
        <DetailDrawer open={detailView} setOpen={setDetailView}>
            <ReservationDetail
                r={reservation}
                close={() => setDetailView(false)}
                accept={accept}
                destroy={() => setDeleteOpen(true)}
                decline={reject}
                modify={() => setModifyTimeOpen(true)}
                create_invoice={() => setBillingOpen(true)}
                download_invoice={downloadInvoice}
                send_invoice={sendInvoice}
            />
        </DetailDrawer>

        <DinoDialog title='Biztos törölni szeretnéd az alábbi foglalást?'
                    subtitle='Csak abban az esetben törölj időpontot ha  azt a klienssel előre leegyeztetted!'
                    open={deleteOpen}
                    handleClose={() => setDeleteOpen(false)}
                    actions={dialogActions}>
            <DialogDetail r={reservation}/>
        </DinoDialog>

        <DinoDialog open={newReservationOpen}
                    title='Új foglalás hozzáadása'
                    subtitle=''
                    handleClose={() => setNewReservationOpen(false)}>
            <ReservationProvider setNewReservationOpen={setNewReservationOpen}>
                <ReserveBase/>
            </ReservationProvider>
        </DinoDialog>

        <DinoDialog open={modifyTimeOpen}
                    title='Időpont módosítása'
                    subtitle='Válassz időpontot!'
                    actions={modifyActions}
                    handleClose={() => setModifyTimeOpen(false)}>
            <SelectOnlyDate reservationData={reservation} setNewDate={setNewDate}/>
        </DinoDialog>

        <DinoDialog open={billingOpen}
                    title='Számla kiállítása'
                    subtitle='A kiállítás előtt módosíthatod az adatokat'
                    actions={billingActions}
                    handleClose={() => setBillingOpen(false)}>
            <InvoiceAdditionalData reference={billingRef}/>
        </DinoDialog>

    </AuthBase>
}

export default Dashboard